import React, { useState, useEffect, useCallback } from 'react';
import { 
  Loader2, 
  ChevronLeft, 
  ChevronRight,
  Download,
  Users,
  DollarSign,
  CreditCard,
  Bus,
  FileText,
  Plus,
  Minus,
  PiggyBank,
  Pencil,
  Trash2,
  Save,
  X,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';

const meses = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' }
];

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
};

const parseCurrency = (value) => {
  if (!value) return 0;
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

const FolhaPagamentoPage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [folha, setFolha] = useState([]);
  const [humans, setHumans] = useState([]);
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [ano, setAno] = useState(new Date().getFullYear());
  
  // Modal de adicionar/editar linha
  const [showModal, setShowModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState({
    human_id: '',
    nome: '',
    salario_base: '',
    extras: '',
    estorno_arredondamento: '',
    descontos: '',
    emprestimo: '',
    vale: '',
    beneficio: '',
    beneficio_lider: '',
    beneficio_extra: '',
    conducao: '',
    observacao: ''
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [folhaRes, humansRes] = await Promise.all([
        api.get(`/folha-manual?mes=${mes}&ano=${ano}`),
        api.get('/humans?tipo_contrato=CLT&status=ativo')
      ]);
      
      let folhaData = folhaRes.data?.linhas || [];
      const humansData = humansRes.data || [];
      
      // Criar Set com IDs dos funcionários já na folha
      const idsNaFolha = new Set(folhaData.map(l => l.human_id));
      
      // Adicionar automaticamente funcionários CLT que não estão na folha
      const linhasAutomaticas = await Promise.all(
        humansData
          .filter(h => !idsNaFolha.has(h.id) && (h.contract_type === 'clt' || h.tipo_contrato === 'CLT'))
          .map(async (human) => {
            // Buscar dados integrados do ponto
            let extrasDetalhes = null;
            let extrasCalculados = 0;
            
            try {
              const integradoRes = await api.get(`/folha-clt/${human.id}/${ano}/${mes}`);
              const integrado = integradoRes.data;
              
              if (integrado && integrado.automatico) {
                extrasDetalhes = integrado.automatico;
                extrasCalculados = 
                  (extrasDetalhes.extras_100?.valor || 0) + 
                  (extrasDetalhes.extras_50?.valor || 0) + 
                  (extrasDetalhes.adicional_noturno?.valor || 0) +
                  (extrasDetalhes.interjornada?.valor || 0) +
                  (extrasDetalhes.intrajornada_suprimida?.valor || 0);
              }
            } catch (e) {
              // Ignorar - funcionário pode não ter dados de ponto
            }
            
            return {
              human_id: human.id,
              nome: human.nome || human.name,
              salario_base: human.salario_base || 0,
              extras: extrasCalculados,
              extras_calculados: extrasCalculados,
              extras_detalhes: extrasDetalhes,
              descontos: 0,
              emprestimo: 0,
              vale: 0,
              beneficio: 0,
              beneficio_lider: 0,
              beneficio_extra: 0,
              conducao: 0,
              estorno_arredondamento: 0,
              observacao: '',
              is_auto: true // Flag para indicar que foi gerado automaticamente
            };
          })
      );
      
      // Para linhas existentes, também buscar dados do ponto
      const folhaComIntegracao = await Promise.all(
        folhaData.map(async (linha) => {
          try {
            const integradoRes = await api.get(`/folha-clt/${linha.human_id}/${ano}/${mes}`);
            const integrado = integradoRes.data;
            
            if (integrado && integrado.automatico) {
              const auto = integrado.automatico;
              const extrasAutomaticos = 
                (auto.extras_100?.valor || 0) + 
                (auto.extras_50?.valor || 0) + 
                (auto.adicional_noturno?.valor || 0) +
                (auto.interjornada?.valor || 0) +
                (auto.intrajornada_suprimida?.valor || 0);
              
              return {
                ...linha,
                extras_calculados: extrasAutomaticos,
                extras_detalhes: auto,
                salario_base: linha.salario_base || integrado.salario_base || 0,
                extras: linha.extras || extrasAutomaticos || 0
              };
            }
          } catch (e) {
            // Silenciosamente ignorar erros de integração
          }
          return linha;
        })
      );
      
      // Combinar linhas existentes com linhas automáticas
      const todasLinhas = [...folhaComIntegracao, ...linhasAutomaticas];
      
      setFolha(todasLinhas);
      setHumans(humansData);
    } catch (error) {
      console.error('Erro ao carregar folha:', error);
      setFolha([]);
    } finally {
      setLoading(false);
    }
  }, [mes, ano]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePrevMonth = () => {
    if (mes === 1) {
      setMes(12);
      setAno(ano - 1);
    } else {
      setMes(mes - 1);
    }
  };

  const handleNextMonth = () => {
    if (mes === 12) {
      setMes(1);
      setAno(ano + 1);
    } else {
      setMes(mes + 1);
    }
  };

  // Calcular totais
  const calcularTotais = () => {
    return folha.reduce((acc, linha) => {
      acc.salario_base += parseCurrency(linha.salario_base);
      acc.extras += parseCurrency(linha.extras);
      acc.estorno += parseCurrency(linha.estorno_arredondamento);
      acc.descontos += parseCurrency(linha.descontos);
      acc.emprestimo += parseCurrency(linha.emprestimo);
      acc.vale += parseCurrency(linha.vale);
      acc.beneficio += parseCurrency(linha.beneficio);
      acc.beneficio_lider += parseCurrency(linha.beneficio_lider);
      acc.beneficio_extra += parseCurrency(linha.beneficio_extra);
      acc.conducao += parseCurrency(linha.conducao);
      return acc;
    }, {
      salario_base: 0,
      extras: 0,
      estorno: 0,
      descontos: 0,
      emprestimo: 0,
      vale: 0,
      beneficio: 0,
      beneficio_lider: 0,
      beneficio_extra: 0,
      conducao: 0
    });
  };

  const totais = calcularTotais();
  
  // Calcular campos derivados
  const calcularVencimentos = (linha) => {
    return parseCurrency(linha.salario_base) + 
           parseCurrency(linha.extras) + 
           parseCurrency(linha.estorno_arredondamento);
  };

  const calcularDescontosTotal = (linha) => {
    return parseCurrency(linha.descontos) + 
           parseCurrency(linha.emprestimo) + 
           parseCurrency(linha.vale);
  };

  const calcularLiquido = (linha) => {
    return calcularVencimentos(linha) - calcularDescontosTotal(linha);
  };

  // Benefícios Caju = benefício + benef. líder + benef. extra + condução (tudo pago pelo cartão Caju)
  const calcularCartao = (linha) => {
    return parseCurrency(linha.beneficio) + 
           parseCurrency(linha.beneficio_lider) + 
           parseCurrency(linha.beneficio_extra) +
           parseCurrency(linha.conducao);
  };

  const calcularTotalGeral = (linha) => {
    return calcularLiquido(linha) + calcularCartao(linha);
  };

  // Abrir modal para nova linha
  const handleNovaLinha = () => {
    setEditingRow(null);
    setFormData({
      human_id: '',
      nome: '',
      salario_base: '',
      extras: '',
      estorno_arredondamento: '',
      descontos: '',
      emprestimo: '',
      vale: '',
      beneficio: '',
      beneficio_lider: '',
      beneficio_extra: '',
      conducao: '',
      observacao: ''
    });
    setShowModal(true);
  };

  // Abrir modal para editar linha
  const handleEditarLinha = (linha, index) => {
    setEditingRow(index);
    setFormData({
      human_id: linha.human_id || '',
      nome: linha.nome || '',
      salario_base: linha.salario_base?.toString() || '',
      extras: linha.extras?.toString() || '',
      estorno_arredondamento: linha.estorno_arredondamento?.toString() || '',
      descontos: linha.descontos?.toString() || '',
      emprestimo: linha.emprestimo?.toString() || '',
      vale: linha.vale?.toString() || '',
      beneficio: linha.beneficio?.toString() || '',
      beneficio_lider: linha.beneficio_lider?.toString() || '',
      beneficio_extra: linha.beneficio_extra?.toString() || '',
      conducao: linha.conducao?.toString() || '',
      observacao: linha.observacao || ''
    });
    setShowModal(true);
  };

  // Salvar linha (nova ou edição)
  const handleSalvarLinha = () => {
    if (!formData.nome.trim()) {
      toast.error('Informe o nome do funcionário');
      return;
    }

    const novaLinha = {
      human_id: formData.human_id || null,
      nome: formData.nome,
      salario_base: parseCurrency(formData.salario_base),
      extras: parseCurrency(formData.extras),
      estorno_arredondamento: parseCurrency(formData.estorno_arredondamento),
      descontos: parseCurrency(formData.descontos),
      emprestimo: parseCurrency(formData.emprestimo),
      vale: parseCurrency(formData.vale),
      beneficio: parseCurrency(formData.beneficio),
      beneficio_lider: parseCurrency(formData.beneficio_lider),
      beneficio_extra: parseCurrency(formData.beneficio_extra),
      conducao: parseCurrency(formData.conducao),
      observacao: formData.observacao
    };

    if (editingRow !== null) {
      // Editar existente
      const novaFolha = [...folha];
      novaFolha[editingRow] = novaLinha;
      setFolha(novaFolha);
    } else {
      // Adicionar nova
      setFolha([...folha, novaLinha]);
    }

    setShowModal(false);
    toast.success(editingRow !== null ? 'Linha atualizada' : 'Linha adicionada');
  };

  // Excluir linha
  const handleExcluirLinha = (index) => {
    if (!window.confirm('Excluir esta linha?')) return;
    const novaFolha = folha.filter((_, i) => i !== index);
    setFolha(novaFolha);
    toast.success('Linha excluída');
  };

  // Salvar folha no servidor
  const handleSalvarFolha = async () => {
    setSaving(true);
    try {
      await api.post('/folha-manual', {
        mes,
        ano,
        linhas: folha
      });
      toast.success('Folha salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar folha:', error);
      toast.error('Erro ao salvar folha');
    } finally {
      setSaving(false);
    }
  };

  // Selecionar funcionário do cadastro
  const handleSelecionarHuman = (humanId) => {
    const human = humans.find(h => h.id === humanId);
    if (human) {
      setFormData({
        ...formData,
        human_id: humanId,
        nome: human.name,
        salario_base: human.salario_base?.toString() || '',
        conducao: human.conducao_mensal?.toString() || ''
      });
    }
  };

  const exportCSV = () => {
    if (folha.length === 0) {
      toast.error('Nenhum dado para exportar');
      return;
    }
    
    const headers = ['Nome', 'Total Geral', 'Salário Base', 'Extras', 'Estorno/Arred.', 'Total Vencimentos', 'Empréstimo', 'Vale', 'Outros Descontos', 'Total Descontos', 'Total Líquido', 'Benefício', 'Benef. Líder', 'Benef. Extra', 'Condução', 'Benefícios Caju', 'Observação'];
    const rows = folha.map(f => [
      f.nome,
      calcularTotalGeral(f),
      f.salario_base,
      f.extras,
      f.estorno_arredondamento,
      calcularVencimentos(f),
      f.emprestimo || 0,
      f.vale || 0,
      f.descontos,
      calcularDescontosTotal(f),
      calcularLiquido(f),
      f.beneficio,
      f.beneficio_lider,
      f.beneficio_extra,
      f.conducao,
      calcularCartao(f),
      f.observacao || ''
    ]);
    
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `folha_${mes}_${ano}.csv`;
    link.click();
    toast.success('CSV exportado!');
  };

  const totalGeralFolha = folha.reduce((acc, linha) => acc + calcularTotalGeral(linha), 0);
  const totalLiquidoFolha = folha.reduce((acc, linha) => acc + calcularLiquido(linha), 0);
  const totalCartaoFolha = folha.reduce((acc, linha) => acc + calcularCartao(linha), 0);
  const totalDescontosFolha = folha.reduce((acc, linha) => acc + calcularDescontosTotal(linha), 0);

  return (
    <div className="space-y-6 p-6" data-testid="folha-pagamento-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Folha de Pagamento CLT
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Lançamento manual de vencimentos, descontos e benefícios
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Navegação de mês */}
          <Button variant="outline" size="icon" onClick={handlePrevMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <Select value={mes.toString()} onValueChange={(v) => setMes(parseInt(v))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {meses.map(m => (
                <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={ano.toString()} onValueChange={(v) => setAno(parseInt(v))}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(a => (
                <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="icon" onClick={handleNextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Ações */}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={handleNovaLinha} data-testid="nova-linha-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nova Linha
        </Button>
        
        <Button 
          onClick={handleSalvarFolha} 
          disabled={saving || folha.length === 0}
          className="bg-green-600 hover:bg-green-700"
          data-testid="salvar-folha-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Salvar Folha
        </Button>

        <Button variant="outline" onClick={exportCSV} disabled={folha.length === 0}>
          <Download className="w-4 h-4 mr-2" />
          CSV
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : (
        <>
          {/* Cards de Totais */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-xs">Total Geral</span>
                </div>
                <p className="text-lg font-bold text-slate-900 dark:text-white">
                  {formatCurrency(totalGeralFolha)}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <Users className="w-4 h-4" />
                  <span className="text-xs">Salários</span>
                </div>
                <p className="text-lg font-bold text-green-600">
                  {formatCurrency(totais.salario_base)}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <Plus className="w-4 h-4" />
                  <span className="text-xs">Extras</span>
                </div>
                <p className="text-lg font-bold text-blue-600">
                  {formatCurrency(totais.extras)}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <Minus className="w-4 h-4" />
                  <span className="text-xs">Descontos</span>
                </div>
                <p className="text-lg font-bold text-red-600">
                  {formatCurrency(totalDescontosFolha)}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <CreditCard className="w-4 h-4" />
                  <span className="text-xs">Benefícios Caju</span>
                </div>
                <p className="text-lg font-bold text-purple-600">
                  {formatCurrency(totalCartaoFolha)}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <Bus className="w-4 h-4" />
                  <span className="text-xs">Condução</span>
                </div>
                <p className="text-lg font-bold text-amber-600">
                  {formatCurrency(totais.conducao)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Tabela de Folha */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <PiggyBank className="w-5 h-5 text-amber-500" />
                Folha {meses.find(m => m.value === mes)?.label}/{ano}
                <Badge variant="outline" className="ml-2">
                  {folha.length} funcionário{folha.length !== 1 ? 's' : ''}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {folha.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Nenhum lançamento para este mês</p>
                  <p className="text-sm mt-1">Clique em "Nova Linha" para adicionar</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                        <TableHead className="font-semibold">Nome</TableHead>
                        <TableHead className="text-right bg-green-50 dark:bg-green-900/20 font-bold">Total Geral</TableHead>
                        <TableHead className="text-right bg-green-50 dark:bg-green-900/20">Salário Base</TableHead>
                        <TableHead className="text-right bg-green-50 dark:bg-green-900/20">Extras</TableHead>
                        <TableHead className="text-right bg-green-50 dark:bg-green-900/20">Estorno/Arred.</TableHead>
                        <TableHead className="text-right bg-green-50 dark:bg-green-900/20 font-semibold">Total Venc.</TableHead>
                        <TableHead className="text-right bg-red-50 dark:bg-red-900/20">Empréstimo</TableHead>
                        <TableHead className="text-right bg-red-50 dark:bg-red-900/20">Vale</TableHead>
                        <TableHead className="text-right bg-red-50 dark:bg-red-900/20">Outros Desc.</TableHead>
                        <TableHead className="text-right bg-red-50 dark:bg-red-900/20 font-semibold">Total Desc.</TableHead>
                        <TableHead className="text-right bg-green-50 dark:bg-green-900/20 font-semibold">Total Líquido</TableHead>
                        <TableHead className="text-right bg-purple-50 dark:bg-purple-900/20 font-semibold">Benefícios Caju</TableHead>
                        <TableHead className="text-center">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {folha.map((linha, index) => (
                        <TableRow key={index} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <TableCell className="font-medium">
                            <div>
                              <span className="text-slate-900 dark:text-white">{linha.nome}</span>
                              {linha.observacao && (
                                <p className="text-[10px] text-slate-400 truncate max-w-[150px]" title={linha.observacao}>
                                  {linha.observacao}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-bold text-slate-900 dark:text-white bg-green-50/50 dark:bg-green-900/10">
                            {formatCurrency(calcularTotalGeral(linha))}
                          </TableCell>
                          <TableCell className="text-right bg-green-50/50 dark:bg-green-900/10">
                            {formatCurrency(linha.salario_base)}
                          </TableCell>
                          <TableCell className="text-right text-blue-600 bg-green-50/50 dark:bg-green-900/10">
                            <div className="flex flex-col items-end gap-0.5">
                              {(() => {
                                // Se tem extras calculados (valor > 0), mostra o valor
                                if (linha.extras > 0) {
                                  return formatCurrency(linha.extras);
                                }
                                
                                // Se tem dados do ponto
                                if (linha.extras_detalhes) {
                                  const temHorasPonto = (linha.extras_detalhes.extras_100?.horas || 0) > 0 || 
                                                        (linha.extras_detalhes.extras_50?.horas || 0) > 0;
                                  
                                  // Se tem horas no ponto mas valor é 0, significa que não tem salário cadastrado
                                  if (temHorasPonto && linha.extras === 0 && !linha.salario_base) {
                                    return <span className="text-amber-500 text-xs">(sem salário)</span>;
                                  }
                                  
                                  // Tem dados do ponto mas sem horas extras (funcionário não fez hora extra)
                                  return <span className="text-slate-400">-</span>;
                                }
                                
                                // Sem dados do ponto
                                return '-';
                              })()}
                              {linha.extras_detalhes && (
                                <span 
                                  className="text-[10px] text-emerald-600 font-medium cursor-help" 
                                  title={`Do ponto: ${linha.extras_detalhes.extras_100?.horas || 0}h (100%) + ${linha.extras_detalhes.extras_50?.horas || 0}h (50%)`}
                                >
                                  ponto
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right bg-green-50/50 dark:bg-green-900/10">
                            {linha.estorno_arredondamento !== 0 ? formatCurrency(linha.estorno_arredondamento) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-semibold bg-green-50/50 dark:bg-green-900/10">
                            {formatCurrency(calcularVencimentos(linha))}
                          </TableCell>
                          <TableCell className="text-right text-red-600 bg-red-50/50 dark:bg-red-900/10">
                            {parseCurrency(linha.emprestimo) > 0 ? formatCurrency(linha.emprestimo) : '-'}
                          </TableCell>
                          <TableCell className="text-right text-red-600 bg-red-50/50 dark:bg-red-900/10">
                            {parseCurrency(linha.vale) > 0 ? formatCurrency(linha.vale) : '-'}
                          </TableCell>
                          <TableCell className="text-right text-red-600 bg-red-50/50 dark:bg-red-900/10">
                            {parseCurrency(linha.descontos) > 0 ? formatCurrency(linha.descontos) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-red-600 bg-red-50/50 dark:bg-red-900/10">
                            {calcularDescontosTotal(linha) > 0 ? formatCurrency(calcularDescontosTotal(linha)) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-semibold bg-green-50/50 dark:bg-green-900/10">
                            {formatCurrency(calcularLiquido(linha))}
                          </TableCell>
                          <TableCell
                            className="text-right font-semibold text-purple-600 bg-purple-50/50 dark:bg-purple-900/10"
                            title={`Benefício: ${formatCurrency(linha.beneficio || 0)} | Extra: ${formatCurrency(linha.beneficio_extra || 0)} | Líder: ${formatCurrency(linha.beneficio_lider || 0)} | Condução: ${formatCurrency(linha.conducao || 0)}`}
                          >
                            {calcularCartao(linha) > 0 ? formatCurrency(calcularCartao(linha)) : '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditarLinha(linha, index)}
                                title="Editar"
                                data-testid={`editar-linha-${index}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleExcluirLinha(index)}
                                title="Excluir"
                                className="text-red-600 hover:bg-red-50"
                                data-testid={`excluir-linha-${index}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      
                      {/* Linha de Totais */}
                      <TableRow className="bg-slate-100 dark:bg-slate-800 font-bold border-t-2">
                        <TableCell>TOTAIS ({folha.length})</TableCell>
                        <TableCell className="text-right">{formatCurrency(totalGeralFolha)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(totais.salario_base)}</TableCell>
                        <TableCell className="text-right text-blue-600">{formatCurrency(totais.extras)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(totais.estorno)}</TableCell>
                        <TableCell className="text-right">-</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(totais.emprestimo)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(totais.vale)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(totais.descontos)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(totalDescontosFolha)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(totalLiquidoFolha)}</TableCell>
                        <TableCell className="text-right text-purple-600">{formatCurrency(totalCartaoFolha)}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Nota de Regime Tributário */}
          <div className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg">
            <strong>Regime Tributário:</strong> Simples Nacional (Super Simples). 
            O custo patronal é apenas <strong>FGTS 8%</strong> + benefícios. 
            INSS patronal 20%, RAT e terceiros já estão inclusos no DAS.
          </div>
        </>
      )}

      {/* Modal de Nova/Editar Linha */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PiggyBank className="w-5 h-5 text-amber-500" />
              {editingRow !== null ? 'Editar Linha' : 'Nova Linha na Folha'}
            </DialogTitle>
            <DialogDescription>
              Preencha os valores manualmente. Campos calculados serão atualizados automaticamente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Seletor de funcionário cadastrado */}
            <div className="space-y-2">
              <Label>Importar do Cadastro (opcional)</Label>
              <Select 
                value={formData.human_id} 
                onValueChange={handleSelecionarHuman}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione para preencher automaticamente..." />
                </SelectTrigger>
                <SelectContent>
                  {humans.map(h => (
                    <SelectItem key={h.id} value={h.id}>
                      {h.name} {h.salario_base ? `(R$ ${h.salario_base})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Ao selecionar, o nome, salário e condução serão preenchidos automaticamente
              </p>
            </div>

            {/* Nome */}
            <div className="space-y-2">
              <Label>Nome do Funcionário *</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Nome completo"
                data-testid="nome-input"
              />
            </div>

            {/* Grid de valores */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-green-600">Salário Base</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.salario_base}
                  onChange={(e) => setFormData({ ...formData, salario_base: e.target.value })}
                  placeholder="0,00"
                  data-testid="salario-base-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-blue-600">Extras (50%/100%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.extras}
                  onChange={(e) => setFormData({ ...formData, extras: e.target.value })}
                  placeholder="0,00"
                  data-testid="extras-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Estorno/Arredondamento</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.estorno_arredondamento}
                  onChange={(e) => setFormData({ ...formData, estorno_arredondamento: e.target.value })}
                  placeholder="0,00"
                  data-testid="estorno-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-red-600">Empréstimo</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.emprestimo}
                  onChange={(e) => setFormData({ ...formData, emprestimo: e.target.value })}
                  placeholder="0,00"
                  data-testid="emprestimo-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-red-600">Vale</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.vale}
                  onChange={(e) => setFormData({ ...formData, vale: e.target.value })}
                  placeholder="0,00"
                  data-testid="vale-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-red-600">Outros Descontos</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.descontos}
                  onChange={(e) => setFormData({ ...formData, descontos: e.target.value })}
                  placeholder="0,00"
                  data-testid="descontos-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-purple-600">Benefício</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.beneficio}
                  onChange={(e) => setFormData({ ...formData, beneficio: e.target.value })}
                  placeholder="0,00"
                  data-testid="beneficio-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-purple-600">Benefício Líder</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.beneficio_lider}
                  onChange={(e) => setFormData({ ...formData, beneficio_lider: e.target.value })}
                  placeholder="0,00"
                  data-testid="beneficio-lider-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-purple-600">Benefício Extra</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.beneficio_extra}
                  onChange={(e) => setFormData({ ...formData, beneficio_extra: e.target.value })}
                  placeholder="0,00"
                  data-testid="beneficio-extra-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-amber-600">Condução</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.conducao}
                  onChange={(e) => setFormData({ ...formData, conducao: e.target.value })}
                  placeholder="0,00"
                  data-testid="conducao-input"
                />
              </div>
            </div>

            {/* Observação */}
            <div className="space-y-2">
              <Label>Observação</Label>
              <Input
                value={formData.observacao}
                onChange={(e) => setFormData({ ...formData, observacao: e.target.value })}
                placeholder="Ex: Aumento salarial a partir de 01/06"
                data-testid="observacao-input"
              />
            </div>

            {/* Preview dos cálculos */}
            <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg space-y-2">
              <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Preview:</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-slate-500">Total Vencimentos:</span>
                  <span className="ml-2 font-medium">
                    {formatCurrency(
                      parseCurrency(formData.salario_base) + 
                      parseCurrency(formData.extras) + 
                      parseCurrency(formData.estorno_arredondamento)
                    )}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Total Descontos:</span>
                  <span className="ml-2 font-medium text-red-600">
                    {formatCurrency(
                      parseCurrency(formData.descontos) +
                      parseCurrency(formData.emprestimo) +
                      parseCurrency(formData.vale)
                    )}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Total Líquido:</span>
                  <span className="ml-2 font-medium">
                    {formatCurrency(
                      parseCurrency(formData.salario_base) + 
                      parseCurrency(formData.extras) + 
                      parseCurrency(formData.estorno_arredondamento) -
                      parseCurrency(formData.descontos) -
                      parseCurrency(formData.emprestimo) -
                      parseCurrency(formData.vale)
                    )}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Benefícios Caju:</span>
                  <span className="ml-2 font-medium text-purple-600">
                    {formatCurrency(
                      parseCurrency(formData.beneficio) + 
                      parseCurrency(formData.beneficio_lider) + 
                      parseCurrency(formData.beneficio_extra) +
                      parseCurrency(formData.conducao)
                    )}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Total Geral:</span>
                  <span className="ml-2 font-bold text-green-600">
                    {formatCurrency(
                      parseCurrency(formData.salario_base) + 
                      parseCurrency(formData.extras) + 
                      parseCurrency(formData.estorno_arredondamento) -
                      parseCurrency(formData.descontos) -
                      parseCurrency(formData.emprestimo) -
                      parseCurrency(formData.vale) +
                      parseCurrency(formData.beneficio) + 
                      parseCurrency(formData.beneficio_lider) + 
                      parseCurrency(formData.beneficio_extra) +
                      parseCurrency(formData.conducao)
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSalvarLinha}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="salvar-linha-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {editingRow !== null ? 'Atualizar' : 'Adicionar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FolhaPagamentoPage;
